import { GlobeAltIcon, MailIcon, SearchIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import type { ReactNode } from 'react';
import {
  FaFacebookMessenger,
  FaLine,
  FaTelegram,
  FaWhatsapp,
} from 'react-icons/fa6';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { tone } from '../../primitives';

export type InboxChannel =
  | 'website'
  | 'email'
  | 'whatsapp'
  | 'messenger'
  | 'line'
  | 'telegram';

/** The channel switch of Agents → Conversations, in its order. */
const SOURCES: { key: InboxChannel; label: string; icon: ReactNode }[] = [
  {
    key: 'website',
    label: 'Web',
    icon: <GlobeAltIcon className='h-4 w-4 text-[#4f46e5]' />,
  },
  {
    key: 'email',
    label: 'Email',
    icon: <MailIcon className='h-4 w-4 text-[#EA4335]' />,
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: <FaWhatsapp className='h-4 w-4 text-[#25D366]' />,
  },
  {
    key: 'messenger',
    label: 'Messenger',
    icon: <FaFacebookMessenger className='h-4 w-4 text-[#0084FF]' />,
  },
  {
    key: 'line',
    label: 'LINE',
    icon: <FaLine className='h-4 w-4 text-[#06C755]' />,
  },
  {
    key: 'telegram',
    label: 'Telegram',
    icon: <FaTelegram className='h-4 w-4 text-[#229ED9]' />,
  },
];

/**
 * The agent's Conversations screen filtered to one channel: the list of
 * threads, and the open one with the customer's messages and the agent's
 * replies.
 */
export default function ConversationsInbox({
  channel,
  brand,
  threads,
  messages,
}: {
  channel: InboxChannel;
  brand: DemoBrand;
  threads: { name: string; preview: string; time: string; avatar: string }[];
  messages: { from: 'customer' | 'agent'; text: string }[];
}) {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');

  return (
    <div
      className={clsxm(
        'overflow-hidden rounded-[22px] text-start shadow-[0_40px_100px_-40px_rgba(0,0,0,0.7)]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex items-center gap-6 border-b border-black/[0.06] px-5 dark:border-white/[0.06]'>
        {[tc('Conversations'), tc('Leads'), tc('Analytics')].map(
          (tab, index) => (
            <span
              key={tab}
              className={clsxm(
                '-mb-px border-b-2 py-3.5 text-sm font-medium',
                index === 0
                  ? 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300'
                  : clsxm('border-transparent', tone.quiet),
              )}
            >
              {tab}
            </span>
          ),
        )}
      </div>

      <div className='grid md:grid-cols-[15.5rem_1fr]'>
        <div className='hidden border-e border-black/[0.06] md:block dark:border-white/[0.06]'>
          <div className='flex gap-1 p-3'>
            {SOURCES.map(({ key, label, icon }) => (
              <span
                key={key}
                className={clsxm(
                  'flex h-8 items-center justify-center gap-1.5 rounded-lg',
                  key === channel
                    ? clsxm(
                        'flex-1 bg-white px-2 text-xs font-semibold shadow-[0_1px_3px_rgba(15,15,40,0.12)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/10 dark:ring-white/10',
                        tone.ink,
                      )
                    : 'w-8 flex-none bg-[#f5f5f8] dark:bg-white/[0.05]',
                )}
              >
                {icon}
                {key === channel ? (
                  <span className='truncate'>
                    {key === 'website'
                      ? t('Web')
                      : key === 'email'
                        ? tc('Email')
                        : label}
                  </span>
                ) : null}
              </span>
            ))}
          </div>
          <div className='px-3 pb-2'>
            <div
              className={clsxm(
                'flex items-center gap-2 rounded-lg bg-[#f5f5f8] px-3 py-2 text-xs dark:bg-white/[0.05]',
                tone.quiet,
              )}
            >
              <SearchIcon className='h-3.5 w-3.5' />
              <span className='truncate'>{tc('Search conversations')}</span>
            </div>
          </div>
          <ul className='pb-2'>
            {threads.map(({ name, preview, time, avatar }, index) => (
              <li
                key={name}
                className={clsxm(
                  'mx-2 flex items-center gap-3 rounded-xl px-3 py-2.5',
                  index === 0 && 'bg-primary-50 dark:bg-primary-400/10',
                )}
              >
                <span
                  className={clsxm(
                    'flex h-8 w-8 flex-none items-center justify-center rounded-full text-[12px] font-semibold text-white',
                    avatar,
                  )}
                >
                  {name.charAt(0)}
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='flex items-baseline justify-between gap-2'>
                    <span
                      className={clsxm(
                        'truncate text-[13px] font-semibold',
                        tone.ink,
                      )}
                    >
                      {name}
                    </span>
                    <span
                      className={clsxm(
                        'flex-none text-[11px] tabular-nums',
                        tone.quiet,
                      )}
                    >
                      {time}
                    </span>
                  </span>
                  <span className={clsxm('block truncate text-xs', tone.body)}>
                    {preview}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className='space-y-3 p-5 text-[13.5px] leading-relaxed sm:p-6'>
          <p
            className={clsxm(
              'flex items-center gap-2 border-b border-black/[0.06] pb-3 text-[13px] font-semibold dark:border-white/[0.06]',
              tone.ink,
            )}
          >
            {SOURCES.find(({ key }) => key === channel)?.icon}
            {threads[0]?.name}
          </p>
          {messages.map(({ from, text }, index) =>
            from === 'customer' ? (
              <div key={index} className='flex justify-end'>
                <p
                  className={clsxm(
                    'max-w-[85%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-3.5 py-2 dark:bg-white/[0.07]',
                    tone.ink,
                  )}
                >
                  {text}
                </p>
              </div>
            ) : (
              <div key={index} className='flex items-end gap-2'>
                <span
                  className={clsxm(
                    'flex h-6 w-6 flex-none items-center justify-center rounded-full text-[10px] font-semibold text-white',
                    brand.bg,
                  )}
                >
                  {brand.initial}
                </span>
                <p
                  className={clsxm(
                    'max-w-[85%] rounded-2xl rounded-es-md px-3.5 py-2 text-white',
                    brand.bg,
                  )}
                >
                  {text}
                </p>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
